/**
 * Custo de UM atendimento a partir das despesas do MÊS.
 *
 * Nasceu no assistente de primeira entrada, onde o médico lista o que gasta por
 * mês (aluguel, secretária, material) e diz quantas consultas faz — o custo de uma
 * consulta é o rateio dos dois. A fórmula saiu de lá para cá quando a mesma conta
 * passou a ser útil no catálogo: duas cópias dela dariam dois resultados diferentes
 * para a mesma pergunta.
 *
 * Tudo em CENTAVOS, como o resto do sistema.
 */

/** Uma linha da lista de despesas mensais. */
export interface ServiceCostItem {
    description: string
    /** Valor mensal, em centavos. */
    amount: number
}

/** Soma das despesas do mês, em centavos. */
export function totalMonthlyCost(costs: ServiceCostItem[]): number {
    return (costs ?? []).reduce((total, cost) => total + Math.max(0, cost.amount || 0), 0)
}

/**
 * Custo de UM atendimento: a despesa mensal rateada pelo volume do mês.
 *
 * Devolve ZERO quando falta qualquer um dos dois lados. Usar a soma crua como custo
 * unitário diria que cada atendimento custa o mês inteiro — R$ 3.800,00 para uma
 * consulta de R$ 400,00 — e jogaria o markup para negativo em qualquer preço
 * realista. Melhor não ter custo do que ter um custo errado por palpite.
 */
export function costPerService(
    costs: ServiceCostItem[],
    monthlyVolume: number
): number {
    const total = totalMonthlyCost(costs)
    if (!total || !monthlyVolume || monthlyVolume <= 0) return 0
    return Math.round(total / monthlyVolume)
}

/**
 * O que costuma compor o custo de um atendimento. São atalhos para preencher a
 * lista, não uma lista fechada — o operador acrescenta o que quiser.
 */
export const COMMON_COST_SUGGESTIONS = [
    'Aluguel da sala',
    'Secretária',
    'Material de consultório',
    'Sistema e telefonia',
    'Contabilidade',
]
